import type { TestRunListItem, TestRunSortField } from '@analyzer/client';
import { TestRunStatus, TestRunTrigger } from '@analyzer/client';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTestRunListQuery, useTestRunReadQuery, useTestRunSummaryQuery } from '../api/test-run.js';

const statusOptions: TestRunStatus[] = [
  TestRunStatus.STARTED,
  TestRunStatus.SUCCESS,
  TestRunStatus.FAILED,
  TestRunStatus.BLOCKED,
  TestRunStatus.SKIPPED,
  TestRunStatus.UNREACHABLE,
  TestRunStatus.CRASHED
];

const triggerOptions: TestRunTrigger[] = [
  TestRunTrigger.MANUAL,
  TestRunTrigger.SCHEDULED,
  TestRunTrigger.RETRY,
  TestRunTrigger.UNKNOWN
];

const failureStatuses: TestRunStatus[] = [
  TestRunStatus.FAILED,
  TestRunStatus.CRASHED,
  TestRunStatus.UNREACHABLE,
  TestRunStatus.BLOCKED
];

const statusColors: Record<TestRunStatus, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  [TestRunStatus.SUCCESS]: 'success',
  [TestRunStatus.FAILED]: 'error',
  [TestRunStatus.BLOCKED]: 'warning',
  [TestRunStatus.UNREACHABLE]: 'error',
  [TestRunStatus.CRASHED]: 'error',
  [TestRunStatus.SKIPPED]: 'info',
  [TestRunStatus.STARTED]: 'default'
};

const statusBarColors: Record<TestRunStatus, string> = {
  [TestRunStatus.SUCCESS]: 'success.main',
  [TestRunStatus.FAILED]: 'error.main',
  [TestRunStatus.BLOCKED]: 'warning.main',
  [TestRunStatus.UNREACHABLE]: 'error.dark',
  [TestRunStatus.CRASHED]: 'error.light',
  [TestRunStatus.SKIPPED]: 'info.main',
  [TestRunStatus.STARTED]: 'grey.400'
};

function parseListParam(value: string | null) {
  if (!value) return [];
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function truncate(text: string | null, max = 64) {
  if (!text) return 'N/A';
  if (text.length <= max) return text;
  return `${text.slice(0, max)}...`;
}

function formatDuration(seconds: number | string | null) {
  if (seconds === null) return 'N/A';
  const value = Number(seconds);
  if (Number.isNaN(value)) return 'N/A';
  return `${value.toFixed(1)}s`;
}

export function TestRunsView() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(() => parseInt(searchParams.get('page') ?? '0', 10) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(() => parseInt(searchParams.get('limit') ?? '25', 10) || 25);
  const [sortField, setSortField] = useState<TestRunSortField>(
    (searchParams.get('sortField') as TestRunSortField) || 'createdDate'
  );
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(
    (searchParams.get('sortOrder') as 'ASC' | 'DESC') || 'DESC'
  );

  const [createdFrom, setCreatedFrom] = useState(searchParams.get('createdFrom') ?? '');
  const [createdTo, setCreatedTo] = useState(searchParams.get('createdTo') ?? '');
  const [status, setStatus] = useState<TestRunStatus[]>(
    parseListParam(searchParams.get('status')).filter((item) =>
      statusOptions.includes(item as TestRunStatus)
    ) as TestRunStatus[]
  );
  const [triggerType, setTriggerType] = useState<TestRunTrigger[]>(
    parseListParam(searchParams.get('triggerType')).filter((item) =>
      triggerOptions.includes(item as TestRunTrigger)
    ) as TestRunTrigger[]
  );
  const [projectId, setProjectId] = useState(searchParams.get('projectId') ?? '');
  const [testId, setTestId] = useState(searchParams.get('testId') ?? '');
  const [onlyFailures, setOnlyFailures] = useState(searchParams.get('onlyFailures') === 'true');
  const [hasErrorMessage, setHasErrorMessage] = useState(searchParams.get('hasErrorMessage') === 'true');
  const [staleMinutes, setStaleMinutes] = useState(30);

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('limit', rowsPerPage.toString());
    params.set('sortField', sortField);
    params.set('sortOrder', sortOrder);
    if (createdFrom) params.set('createdFrom', createdFrom);
    if (createdTo) params.set('createdTo', createdTo);
    if (status.length) params.set('status', status.join(','));
    if (triggerType.length) params.set('triggerType', triggerType.join(','));
    if (projectId) params.set('projectId', projectId);
    if (testId) params.set('testId', testId);
    if (onlyFailures) params.set('onlyFailures', 'true');
    if (hasErrorMessage) params.set('hasErrorMessage', 'true');
    setSearchParams(params, { replace: true });
  }, [
    page,
    rowsPerPage,
    sortField,
    sortOrder,
    createdFrom,
    createdTo,
    status,
    triggerType,
    projectId,
    testId,
    onlyFailures,
    hasErrorMessage,
    setSearchParams
  ]);

  const listQueryParams = useMemo(
    () => ({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      sortField,
      sortOrder,
      createdFrom: createdFrom ? dayjs(createdFrom).startOf('day').toISOString() : undefined,
      createdTo: createdTo ? dayjs(createdTo).endOf('day').toISOString() : undefined,
      status: status.length ? status.join(',') : undefined,
      triggerType: triggerType.length ? triggerType.join(',') : undefined,
      projectId: projectId || undefined,
      testId: testId || undefined,
      onlyFailures: onlyFailures || undefined,
      hasErrorMessage: hasErrorMessage || undefined
    }),
    [
      rowsPerPage,
      page,
      sortField,
      sortOrder,
      createdFrom,
      createdTo,
      status,
      triggerType,
      projectId,
      testId,
      onlyFailures,
      hasErrorMessage
    ]
  );

  const summaryQueryParams = useMemo(
    () => ({
      createdFrom: listQueryParams.createdFrom,
      createdTo: listQueryParams.createdTo,
      status: listQueryParams.status,
      triggerType: listQueryParams.triggerType,
      projectId: listQueryParams.projectId,
      testId: listQueryParams.testId,
      onlyFailures: listQueryParams.onlyFailures,
      hasErrorMessage: listQueryParams.hasErrorMessage
    }),
    [listQueryParams]
  );

  const { data, isLoading, isError, error } = useTestRunListQuery(listQueryParams);
  const { data: summary } = useTestRunSummaryQuery(summaryQueryParams);
  const { data: selectedRun, isLoading: isRunLoading, error: runError } = useTestRunReadQuery(selectedRunId);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (field: TestRunSortField) => {
    const isAsc = sortField === field && sortOrder === 'ASC';
    setSortField(field);
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setPage(0);
  };

  const handleOnlyFailures = (checked: boolean) => {
    setOnlyFailures(checked);
    if (checked) {
      setStatus(failureStatuses);
    }
    setPage(0);
  };

  const handleClearFilters = () => {
    setCreatedFrom('');
    setCreatedTo('');
    setStatus([]);
    setTriggerType([]);
    setProjectId('');
    setTestId('');
    setOnlyFailures(false);
    setHasErrorMessage(false);
    setPage(0);
  };

  const totalRuns = summary?.totalRuns ?? 0;
  const failureRate = summary?.failureRate ?? 0;
  const avgDurationSeconds = summary?.avgDurationSeconds ?? null;
  const statusCounts = summary?.statusCounts ?? ({} as Record<TestRunStatus, number>);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4">Test Runs</Typography>
          <Typography variant="body2" color="text.secondary">
            Filter and inspect recent test execution history.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Runs
                </Typography>
                <Typography variant="h5">{totalRuns}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Failure Rate
                </Typography>
                <Typography variant="h5">{(failureRate * 100).toFixed(1)}%</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Avg Duration
                </Typography>
                <Typography variant="h5">
                  {avgDurationSeconds === null ? 'N/A' : formatDuration(avgDurationSeconds)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Status Mix
                </Typography>
                <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', mt: 1 }}>
                  {statusOptions.map((item) => (
                    <Box
                      key={item}
                      sx={{
                        flex: statusCounts[item] || 0,
                        backgroundColor: statusBarColors[item]
                      }}
                    />
                  ))}
                </Box>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  {statusOptions.map((item) => (
                    <Typography key={item} variant="caption" color="text.secondary">
                      {item}: {statusCounts[item] || 0}
                    </Typography>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Created From"
                type="date"
                value={createdFrom}
                onChange={(event) => {
                  setCreatedFrom(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Created To"
                type="date"
                value={createdTo}
                onChange={(event) => {
                  setCreatedTo(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  label="Status"
                  value={status}
                  renderValue={(selected) => selected.join(', ')}
                  onChange={(event) => {
                    const value = event.target.value as TestRunStatus[];
                    setStatus(value);
                    setPage(0);
                  }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={status.includes(option)} />
                      <ListItemText primary={option} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Trigger Type</InputLabel>
                <Select
                  multiple
                  label="Trigger Type"
                  value={triggerType}
                  renderValue={(selected) => selected.join(', ')}
                  onChange={(event) => {
                    const value = event.target.value as TestRunTrigger[];
                    setTriggerType(value);
                    setPage(0);
                  }}
                >
                  {triggerOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Checkbox checked={triggerType.includes(option)} />
                      <ListItemText primary={option} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Project ID"
                value={projectId}
                onChange={(event) => {
                  setProjectId(event.target.value);
                  setPage(0);
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Test ID"
                value={testId}
                onChange={(event) => {
                  setTestId(event.target.value);
                  setPage(0);
                }}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyFailures}
                    onChange={(event) => handleOnlyFailures(event.target.checked)}
                  />
                }
                label="Only Failures"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasErrorMessage}
                    onChange={(event) => {
                      setHasErrorMessage(event.target.checked);
                      setPage(0);
                    }}
                  />
                }
                label="Has Error Message"
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {isLoading && (
          <Backdrop open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}

        {isError && <Alert severity="error">{error?.message || 'Failed to load test runs.'}</Alert>}

        {!isLoading && !isError && data?.data?.length === 0 && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">No test runs found for the current filters.</Typography>
          </Paper>
        )}

        {!isLoading && !isError && data?.data?.length ? (
          <Paper>
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'createdDate'}
                        direction={
                          sortField === 'createdDate' ? (sortOrder === 'ASC' ? 'asc' : 'desc') : 'desc'
                        }
                        onClick={() => handleRequestSort('createdDate')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'status'}
                        direction={sortField === 'status' ? (sortOrder === 'ASC' ? 'asc' : 'desc') : 'desc'}
                        onClick={() => handleRequestSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'durationSeconds'}
                        direction={
                          sortField === 'durationSeconds' ? (sortOrder === 'ASC' ? 'asc' : 'desc') : 'desc'
                        }
                        onClick={() => handleRequestSort('durationSeconds')}
                      >
                        Duration
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Test</TableCell>
                    <TableCell>Proxy</TableCell>
                    <TableCell>Vantage</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.data.map((run: TestRunListItem) => (
                    <TableRow key={run.id}>
                      <TableCell>{new Date(run.createdDate).toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip label={run.status} color={statusColors[run.status]} size="small" />
                      </TableCell>
                      <TableCell>{formatDuration(run.durationSeconds)}</TableCell>
                      <TableCell>{run.triggerType}</TableCell>
                      <TableCell>{run.projectId}</TableCell>
                      <TableCell>{run.testId}</TableCell>
                      <TableCell>{run.usedProxy || 'N/A'}</TableCell>
                      <TableCell>{run.vantagePoint || 'N/A'}</TableCell>
                      <TableCell>
                        <Tooltip title={run.errorMessage || ''} placement="top" arrow>
                          <span>{truncate(run.errorMessage, 48)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" onClick={() => setSelectedRunId(run.id)}>
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50]}
                      count={data.metadata.total}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      showFirstButton={true}
                      showLastButton={true}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Paper>
        ) : null}
      </Stack>

      <Drawer anchor="right" open={selectedRunId !== null} onClose={() => setSelectedRunId(null)}>
        <Box sx={{ width: 420, p: 3 }}>
          <Typography variant="h6">Test Run Details</Typography>
          <Divider sx={{ my: 2 }} />

          {isRunLoading && <CircularProgress />}
          {runError && <Alert severity="error">Failed to load test run.</Alert>}

          {selectedRun && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Status</Typography>
              <Chip label={selectedRun.status} color={statusColors[selectedRun.status]} size="small" />

              <Typography variant="subtitle2">Timestamps</Typography>
              <Typography variant="body2">
                Created: {new Date(selectedRun.createdDate).toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Updated: {new Date(selectedRun.updatedDate).toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Started: {selectedRun.startDate ? new Date(selectedRun.startDate).toLocaleString() : 'N/A'}
              </Typography>
              <Typography variant="body2">
                Finished:{' '}
                {selectedRun.finishedDate ? new Date(selectedRun.finishedDate).toLocaleString() : 'N/A'}
              </Typography>

              <Typography variant="subtitle2">Computed</Typography>
              <Typography variant="body2">
                Finished-Start Delta:{' '}
                {selectedRun.startDate && selectedRun.finishedDate
                  ? `${dayjs(selectedRun.finishedDate)
                      .diff(dayjs(selectedRun.startDate), 'second', true)
                      .toFixed(1)}s`
                  : 'N/A'}
              </Typography>
              <Typography variant="body2">
                Stale Started:{' '}
                {selectedRun.status === TestRunStatus.STARTED &&
                dayjs().diff(dayjs(selectedRun.createdDate), 'minute') > staleMinutes
                  ? 'Yes'
                  : 'No'}
              </Typography>
              <TextField
                label="Stale Threshold (minutes)"
                type="number"
                value={staleMinutes}
                onChange={(event) => {
                  const value = parseInt(event.target.value, 10);
                  setStaleMinutes(Number.isFinite(value) ? value : 30);
                }}
                size="small"
              />

              <Typography variant="subtitle2">Identifiers</Typography>
              <Typography variant="body2">Run ID: {selectedRun.id}</Typography>
              <Typography variant="body2">Project ID: {selectedRun.projectId}</Typography>
              <Typography variant="body2">Test ID: {selectedRun.testId}</Typography>
              <Typography variant="body2">Snapshot ID: {selectedRun.snapshotId || 'N/A'}</Typography>

              <Typography variant="subtitle2">Error Message</Typography>
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Typography variant="body2" component="pre">
                  {selectedRun.errorMessage || 'N/A'}
                </Typography>
              </Paper>

              <Typography variant="subtitle2">Full JSON</Typography>
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Typography variant="body2" component="pre">
                  {JSON.stringify(selectedRun, null, 2)}
                </Typography>
              </Paper>
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
